import { useCallback, useRef, useState } from "react";
import type { AxiosResponse } from "axios";
import { useSnackbar } from "notistack";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { runBookingWriteWithCancelConfirm } from "../../../components/BookingForm/BookingConfirmStrategy/runBookingWriteWithCancelConfirm.function";
import type { AffectedExistingBooking } from "../../Booking-Form/typings/cancel-existing-booking.types";
import { newNoPlaceZoneBooking, newPlaceBooking } from "../../../utils/axios-requests";
import {
  buildColleagueConferenceBookingDto,
  buildColleaguePlaceBookingDto,
  colleagueFloorBookConfirmCopy
} from "./colleague-workplace-floor.functions";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import type {
  ColleagueFloorConferenceDraft,
  ColleagueWorkplaceFloorSession
} from "./colleague-workplace-floor.types";

type ColleagueFloorBooker = Pick<TeamMember, "userId" | "firstName" | "surname" | "email">;

type PlaceBookRequest = {
  inventoryId: number;
  placeTypeId?: number;
};

/**
 * Desk and room saves started from the colleague floor.
 * Uses the booking form's preview: overlapping bookings are listed before they are changed.
 */
type UseColleagueFloorBookingWritesArgs = {
  session: ColleagueWorkplaceFloorSession;
  user: ColleagueFloorBooker;
  bookingTimezone: string;
  onBooked: () => void;
};

function withCancelConfirmation<T extends object>(
  dto: T,
  confirmCancelExistingBookings?: boolean
): T {
  if (confirmCancelExistingBookings) {
    return { ...dto, confirmCancelExistingBookings: true };
  }
  return dto;
}

export function useColleagueFloorBookingWrites({
  session,
  user,
  bookingTimezone,
  onBooked
}: UseColleagueFloorBookingWritesArgs) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [affectedExistingBookings, setAffectedExistingBookings] = useState<
    AffectedExistingBooking[]
  >([]);
  const [showCancelExistingConfirm, setShowCancelExistingConfirm] = useState(false);
  const [isConfirmingCancelExisting, setIsConfirmingCancelExisting] = useState(false);
  const retryRef = useRef<(() => void) | null>(null);
  /** Set in the click handler, before React re-renders, so a second click cannot post again. */
  const writeInFlightRef = useRef(false);
  /** Set when the overlap list is showing, before that state re-renders. */
  const promptOpenRef = useRef(false);

  const reportError = useCallback(() => {
    writeInFlightRef.current = false;
    promptOpenRef.current = false;
    retryRef.current = null;
    setShowCancelExistingConfirm(false);
    setAffectedExistingBookings([]);
    setIsConfirmingCancelExisting(false);
    enqueueSnackbar(t("There was a booking error"), { variant: "error" });
  }, [enqueueSnackbar, t]);

  const clearCancelExistingPrompt = useCallback(() => {
    writeInFlightRef.current = false;
    promptOpenRef.current = false;
    setShowCancelExistingConfirm(false);
    setAffectedExistingBookings([]);
    setIsConfirmingCancelExisting(false);
    retryRef.current = null;
  }, []);

  const rememberCancelExistingPreview = useCallback(
    (affected: AffectedExistingBooking[], retry: () => void) => {
      retryRef.current = retry;
      promptOpenRef.current = true;
      writeInFlightRef.current = false;
      setAffectedExistingBookings(affected);
      setShowCancelExistingConfirm(true);
      setIsConfirmingCancelExisting(false);
    },
    []
  );

  const beginWrite = useCallback(() => {
    if (writeInFlightRef.current || promptOpenRef.current) {
      return false;
    }
    writeInFlightRef.current = true;
    return true;
  }, []);

  const writeWithCancelConfirm = useCallback(
    (
      post: (confirmCancelExistingBookings?: boolean) => Promise<AxiosResponse<unknown>>,
      onSuccess: () => void
    ) =>
      runBookingWriteWithCancelConfirm(post, {
        onSuccess: () => {
          clearCancelExistingPrompt();
          onSuccess();
        },
        onError: reportError,
        onCancelExistingConfirmationRequired: rememberCancelExistingPreview
      }),
    [clearCancelExistingPrompt, rememberCancelExistingPreview, reportError]
  );

  const bookMutation = useMutation(async (request: PlaceBookRequest) => {
    if (session.locationInventoryId == null) {
      writeInFlightRef.current = false;
      return;
    }
    const dto = buildColleaguePlaceBookingDto({
      user,
      locationInventoryId: session.locationInventoryId,
      inventoryId: request.inventoryId,
      ...(request.placeTypeId != null ? { placeTypeId: request.placeTypeId } : {}),
      date: session.date,
      startTime: session.startTime,
      endTime: session.endTime
    });
    await writeWithCancelConfirm(
      confirmCancelExistingBookings =>
        newPlaceBooking(withCancelConfirmation(dto, confirmCancelExistingBookings)),
      () => {
        enqueueSnackbar(t(colleagueFloorBookConfirmCopy(request.placeTypeId).successKey), {
          variant: "success"
        });
        onBooked();
      }
    );
  });

  const conferenceMutation = useMutation(async (draft: ColleagueFloorConferenceDraft) => {
    if (session.locationInventoryId == null) {
      writeInFlightRef.current = false;
      return;
    }
    const dto = buildColleagueConferenceBookingDto({
      user,
      locationInventoryId: session.locationInventoryId,
      locationName: session.locationName,
      timezone: bookingTimezone,
      date: session.date,
      startTime: session.startTime,
      endTime: session.endTime,
      draft
    });
    await writeWithCancelConfirm(
      confirmCancelExistingBookings =>
        newNoPlaceZoneBooking(withCancelConfirmation(dto, confirmCancelExistingBookings)),
      () => {
        enqueueSnackbar(t("_zoneBookingSuccess"), { variant: "success" });
        onBooked();
      }
    );
  });

  const confirmInventory = useCallback(
    (inventoryId: number, placeTypeId?: number) => {
      if (!beginWrite()) {
        return;
      }
      bookMutation.mutate({ inventoryId, placeTypeId });
    },
    [beginWrite, bookMutation]
  );

  const confirmConference = useCallback(
    (draft: ColleagueFloorConferenceDraft) => {
      if (!beginWrite()) {
        return;
      }
      conferenceMutation.mutate(draft);
    },
    [beginWrite, conferenceMutation]
  );

  const dismissCancelExisting = useCallback(() => {
    if (writeInFlightRef.current) {
      return;
    }
    clearCancelExistingPrompt();
  }, [clearCancelExistingPrompt]);

  const proceedCancelExisting = useCallback(() => {
    if (writeInFlightRef.current) {
      return;
    }
    const retry = retryRef.current;
    if (retry == null) {
      return;
    }
    retryRef.current = null;
    writeInFlightRef.current = true;
    promptOpenRef.current = true;
    setShowCancelExistingConfirm(false);
    setIsConfirmingCancelExisting(true);
    retry();
  }, []);

  return {
    confirmInventory,
    confirmConference,
    isConferenceSubmitting: conferenceMutation.isLoading || isConfirmingCancelExisting,
    blocksParentClose: showCancelExistingConfirm || isConfirmingCancelExisting,
    showCancelExistingConfirm,
    affectedExistingBookings,
    isConfirmingCancelExisting,
    dismissCancelExisting,
    proceedCancelExisting
  };
}
